import { useMemo, useState } from 'react';
import { useApp } from '../../lib/app';
import { supabase } from '../../lib/supabase';
import { useRows } from '../../lib/useRows';
import { Badge, Button, Card, Empty } from '../../ui/kit';
import { RecordDrawer, type FieldSpec } from '../../ui/RecordDrawer';

type S = Record<string, unknown> & { id: string; name: string; short_code: string; client_org_id: string | null; active: boolean; deleted_at: string | null };
type P = Record<string, unknown> & { id: string; site_id: string; name: string; code: string | null; status: string; deleted_at: string | null };

export default function Sites() {
  const { access, reload, siteId, can } = useApp();
  const isSuper = Boolean(access?.super_admin);
  const [openSite, setOpenSite] = useState<S | null | 'new'>(null);
  const [openProject, setOpenProject] = useState<P | null | 'new'>(null);
  const sites = useRows<S>(() => supabase.from('sites').select('*').order('sort').order('name'), [], ['sites']);
  const projects = useRows<P>(() => supabase.from('projects').select('*').order('sort'), [], ['projects']);
  const orgs = useRows<{ id: string; name: string }>(() => supabase.from('organisations').select('id, name').is('deleted_at', null).order('name'), []);

  const siteFields = useMemo<FieldSpec[]>(
    () => [
      { key: 'name', label: 'Name', required: true, hint: 'e.g. Tavarua Island Resort — Construction' },
      { key: 'short_code', label: 'Short code', required: true, hint: 'e.g. TAV' },
      { key: 'client_org_id', label: 'Client', type: 'select', options: orgs.rows.map((o) => ({ value: o.id, label: o.name })) },
      { key: 'drive_folder_url', label: 'Client Google Drive folder', type: 'url' },
      { key: 'active', label: 'Active', type: 'checkbox' },
      { key: 'sort', label: 'Order in the list', type: 'number' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    [orgs.rows],
  );
  const projectFields = useMemo<FieldSpec[]>(
    () => [
      { key: 'name', label: 'Name', required: true },
      { key: 'code', label: 'Code' },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        options: [
          { value: 'planning', label: 'Planning' },
          { value: 'active', label: 'Active' },
          { value: 'on_hold', label: 'On hold' },
          { value: 'complete', label: 'Complete' },
        ],
      },
      { key: 'start_on', label: 'Start', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    [],
  );

  return (
    <>
      <Card title="Clients / sites" actions={isSuper ? <Button kind="primary" onClick={() => setOpenSite('new')}>+ Add site</Button> : null} pad={false}>
        {sites.loading ? (
          <Empty>Loading…</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Code</th>
                  <th>Client</th>
                  <th>Projects</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sites.rows.map((s) => (
                  <tr key={s.id} className={`click ${s.deleted_at ? 'deleted' : ''}`} onClick={() => setOpenSite(s)}>
                    <td>
                      <strong>{s.name}</strong>
                    </td>
                    <td>{s.short_code}</td>
                    <td>{orgs.rows.find((o) => o.id === s.client_org_id)?.name ?? '—'}</td>
                    <td>{projects.rows.filter((p) => p.site_id === s.id && !p.deleted_at).map((p) => p.name).join(', ') || '—'}</td>
                    <td>{s.active ? <Badge tone="good">Active</Badge> : <Badge>Inactive</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Projects on the selected site"
        actions={can('buildings', 4) ? <Button kind="primary" onClick={() => setOpenProject('new')}>+ Add project</Button> : null}
        pad={false}
      >
        {projects.rows.filter((p) => p.site_id === siteId).length === 0 ? (
          <Empty>No projects on this site.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Code</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {projects.rows
                  .filter((p) => p.site_id === siteId)
                  .map((p) => (
                    <tr key={p.id} className={`click ${p.deleted_at ? 'deleted' : ''}`} onClick={() => setOpenProject(p)}>
                      <td>
                        <strong>{p.name}</strong>
                      </td>
                      <td>{p.code ?? '—'}</td>
                      <td>
                        <Badge>{p.status}</Badge>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RecordDrawer
        open={openSite !== null}
        onClose={() => setOpenSite(null)}
        title={openSite === 'new' ? 'Add client / site' : openSite ? openSite.name : ''}
        table="sites"
        fields={siteFields}
        record={openSite === 'new' ? null : openSite}
        defaults={{ active: true, sort: 100 }}
        canEdit={isSuper}
        onSaved={() => {
          void sites.reload();
          void reload();
        }}
        label={(r) => `site ${String(r.name ?? '')}`}
      />
      <RecordDrawer
        open={openProject !== null}
        onClose={() => setOpenProject(null)}
        title={openProject === 'new' ? 'Add project' : openProject ? openProject.name : ''}
        table="projects"
        fields={projectFields}
        record={openProject === 'new' ? null : openProject}
        defaults={{ site_id: siteId, status: 'active' }}
        canEdit={can('buildings', 4)}
        onSaved={() => void projects.reload()}
        label={(r) => `project ${String(r.name ?? '')}`}
      />
    </>
  );
}
